import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Project } from '../core/model';
import type { AssetMeta, ProjectSummary } from './api';

export const DB_NAME = 'ohmyobs';
export const DB_VERSION = 1;

export interface AssetRecord extends AssetMeta {
  /** Hex SHA-256 of the content; the de-duplication key. */
  hash: string;
  storage: 'opfs' | 'idb';
}

interface StudioDb extends DBSchema {
  projects: { key: string; value: Project };
  /** Written in the same transaction as the document so listing never loads documents. */
  projectSummaries: { key: string; value: ProjectSummary; indexes: { byUpdated: number } };
  meta: { key: string; value: unknown };
  assets: { key: string; value: AssetRecord; indexes: { byHash: string } };
  /** Blob storage used only when OPFS is unavailable. */
  assetBlobs: { key: string; value: Blob };
}

export type StudioDbHandle = IDBPDatabase<StudioDb>;

/**
 * Opens the database lazily and re-opens after another tab upgrades or deletes it.
 * Each store instance owns its connection.
 */
export function createDbOpener(): () => Promise<StudioDbHandle> {
  let pending: Promise<StudioDbHandle> | null = null;
  return () => {
    pending ??= openDB<StudioDb>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('projects', { keyPath: 'id' });
          db.createObjectStore('projectSummaries', { keyPath: 'id' }).createIndex('byUpdated', 'updatedAt');
          db.createObjectStore('meta');
          db.createObjectStore('assets', { keyPath: 'id' }).createIndex('byHash', 'hash');
          db.createObjectStore('assetBlobs');
        }
      },
      blocking() {
        void pending?.then((db) => db.close());
        pending = null;
      },
      terminated() {
        pending = null;
      },
    }).catch((err) => {
      pending = null;
      throw err;
    });
    return pending;
  };
}
